<script setup lang="ts">
import { AFFILIATE, AFFILIATE_DISCOUNT, AFFILIATE_DISCOUNT_CODE_PATTERN, discountOptions, normalizeDiscountCode, suggestDiscountCode } from '~/constants/affiliate'

/**
 * The partner's own discount code (server/utils/affiliateDiscount.ts): made,
 * changed or deleted here, and copied to hand out. A buyer who types it at
 * checkout gets the discount, and the order is credited to the partner even
 * without a click on their link.
 */
const props = defineProps<{
  /** Their link code, for the first suggestion. */
  code: string
  discountCode: string | null
  /** The discount the code gives, chosen by the partner. */
  discountPercent: number | null
  /** The highest discount they may choose: the usual one, or more if an admin raised it. */
  maxPercent: number
  /** The terms that describe codes are not accepted yet. */
  needsTerms: boolean
}>()

const emit = defineEmits<{ (e: 'saved'): void }>()

const { t } = useI18n()
const { show: showSnackbar } = useSnackbar()
const rate = Math.round(AFFILIATE.commissionRate * 100)

const editing = ref(false)
const draft = ref('')
const percent = ref<number>(AFFILIATE_DISCOUNT.default)
const options = computed(() => discountOptions(props.maxPercent))
const saving = ref(false)
const error = ref('')

watch(() => [props.discountCode, props.discountPercent] as const, ([code, current]) => {
  editing.value = !code
  // A limit lowered below an existing code's discount offers the new limit instead.
  percent.value = Math.min(current ?? AFFILIATE_DISCOUNT.default, props.maxPercent)
  draft.value = code ?? suggestDiscountCode(props.code, percent.value)
  error.value = ''
}, { immediate: true })

// A suggested code follows the percentage ("JANA20" → "JANA30"); one the partner typed stays.
watch(percent, (value, previous) => {
  if (draft.value === suggestDiscountCode(props.code, previous)) draft.value = suggestDiscountCode(props.code, value)
})

/** What the partner earns, as a share of the full price, with this discount. */
const effectiveRate = computed(() => Math.round(rate * (100 - percent.value) / 100 * 10) / 10)

const cleaned = computed(() => draft.value.trim().toUpperCase().replace(/[\s-]+/g, ''))
const valid = computed(() => AFFILIATE_DISCOUNT_CODE_PATTERN.test(cleaned.value))
const unchanged = computed(() => cleaned.value === props.discountCode && percent.value === props.discountPercent)

async function save() {
  const code = normalizeDiscountCode(draft.value)
  if (!code) {
    error.value = t('affiliate.discountCode.error.INVALID_CODE')
    return
  }
  saving.value = true
  error.value = ''
  try {
    await $fetch('/api/affiliate/discount-code', { method: 'PUT', body: { code, percent: percent.value } })
    showSnackbar(t('affiliate.discountCode.saved'))
    emit('saved')
  } catch (err: unknown) {
    const reason = (err as { data?: { statusMessage?: string } })?.data?.statusMessage ?? ''
    error.value = t(`affiliate.discountCode.error.${['INVALID_CODE', 'INVALID_PERCENT', 'CODE_TAKEN', 'NEEDS_TERMS'].includes(reason) ? reason : 'generic'}`)
  } finally {
    saving.value = false
  }
}

async function remove() {
  if (!window.confirm(t('affiliate.discountCode.deleteConfirm'))) return
  saving.value = true
  try {
    await $fetch('/api/affiliate/discount-code', { method: 'DELETE' })
    emit('saved')
  } catch {
    error.value = t('affiliate.discountCode.error.generic')
  } finally {
    saving.value = false
  }
}

async function copy() {
  if (!props.discountCode) return
  try {
    await navigator.clipboard.writeText(props.discountCode)
    showSnackbar(t('affiliate.discountCode.copied'))
  } catch {
    // Clipboard blocked: the code is on screen to type.
  }
}
</script>

<template>
  <div class="rounded-[0.625rem] border border-line p-6 dark:border-gray-700">
    <h2 class="!text-xl">{{ t('affiliate.discountCode.title') }}</h2>
    <p class="mt-2 text-sm text-muted dark:text-gray-400">{{ t('affiliate.discountCode.intro', { max: maxPercent }) }}</p>

    <SofaxNotice v-if="needsTerms" type="info" class="mt-4">{{ t('affiliate.discountCode.needsTerms') }}</SofaxNotice>

    <div v-else-if="discountCode && !editing" class="mt-4 flex flex-wrap items-center gap-3">
      <span class="rounded-lg border-2 border-dashed border-accent px-4 py-2 font-mono text-xl font-bold tracking-wider text-ink dark:text-white">{{ discountCode }}</span>
      <span class="rounded-full bg-accent-soft/40 px-3 py-1 text-sm font-semibold text-ink dark:bg-accent/20 dark:text-white">−{{ discountPercent }} %</span>
      <SofaxBtn :label="t('affiliate.discountCode.copy')" variant="dark" @click="copy" />
      <button type="button" class="link text-sm" :disabled="saving" @click="editing = true">{{ t('affiliate.discountCode.change') }}</button>
      <button type="button" class="link text-sm !text-red-600 dark:!text-red-400" :disabled="saving" @click="remove">{{ t('affiliate.discountCode.delete') }}</button>
    </div>

    <form v-else class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start" @submit.prevent="save">
      <SofaxSelect v-model.number="percent" class="sm:w-36" :label="t('affiliate.discountCode.percentLabel')">
        <option v-for="option in options" :key="option" :value="option">{{ option }} %</option>
      </SofaxSelect>
      <SofaxInput
        v-model="draft"
        class="flex-1"
        :label="t('affiliate.discountCode.label')"
        :hint="t('affiliate.discountCode.hint')"
        :error="error"
        :maxlength="24"
      />
      <div class="flex gap-2 sm:mt-7">
        <SofaxBtn type="submit" :label="discountCode ? t('affiliate.discountCode.saveChange') : t('affiliate.discountCode.create')" :disabled="saving || !valid || unchanged" />
        <SofaxBtn v-if="discountCode" :label="t('affiliate.discountCode.cancel')" variant="outline" @click="editing = false; draft = discountCode; percent = discountPercent ?? AFFILIATE_DISCOUNT.default; error = ''" />
      </div>
    </form>

    <p v-if="!needsTerms && (editing || !discountCode)" class="mt-2 text-sm text-body dark:text-gray-300">
      {{ t('affiliate.discountCode.effective', { percent, rate, effective: effectiveRate }) }}
    </p>
    <p v-if="discountCode && !editing && error" class="mt-2 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
    <p v-if="discountCode && editing" class="mt-2 text-xs text-muted dark:text-gray-400">{{ t('affiliate.discountCode.changeHint', { code: discountCode }) }}</p>
  </div>
</template>
